import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Sparkles, X, ChevronRight } from "lucide-react";
import { useVectorContext } from "@/hooks/useVectorContext";
import { ragQuery } from "@/lib/ragQuery.functions";
import { getLanguageFromUrl } from "@/hooks/useLanguageRouting";

const ContextualDiscovery = () => {
  const { t } = useTranslation("home");
  const location = useLocation();
  const language = getLanguageFromUrl(location.pathname);
  const { search, isLoading: isSearching } = useVectorContext();

  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [insight, setInsight] = useState<string | null>(null);
  const [isLoadingInsight, setIsLoadingInsight] = useState(false);
  const [faqSchema, setFaqSchema] = useState<object | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTriggered = useRef(false);

  useEffect(() => {
    hasTriggered.current = false;
    setIsVisible(false);
    setIsDismissed(false);
    setInsight(null);

    timerRef.current = setTimeout(() => {
      if (!hasTriggered.current && !isDismissed) {
        hasTriggered.current = true;
        triggerDiscovery();
      }
    }, 20000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [location.pathname]);

  const triggerDiscovery = useCallback(async () => {
    const pageText = document.querySelector("main")?.textContent?.slice(0, 1000) || "";
    if (!pageText.trim()) return;

    setIsVisible(true);
    setIsLoadingInsight(true);

    try {
      const nodes = await search(pageText, language);
      if (!nodes || nodes.length === 0) {
        setIsLoadingInsight(false);
        return;
      }

      const data = await ragQuery({
        data: {
          nodes: nodes.slice(0, 3),
          userQuery: `Based on the current page about ${location.pathname.replace(/\//g, " ").trim() || "nearshoring"}, provide a relevant business insight.`,
          language,
        },
      });

      setInsight(data?.answer || null);
      setFaqSchema((data?.faqSchema as object | null) || null);
    } catch (e) {
      console.error("Discovery error:", e);
    } finally {
      setIsLoadingInsight(false);
    }
  }, [language, location.pathname, search]);

  const dismiss = () => {
    setIsDismissed(true);
    setIsVisible(false);
  };

  if (!isVisible || isDismissed) return null;

  return (
    <>
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      <div
        className="fixed bottom-6 right-6 z-50 max-w-sm animate-fade-in"
        role="complementary"
        aria-label={language === "de" ? "KI-gestützter Einblick" : "AI-powered insight"}
      >
        <div className="rounded-2xl glass-panel-strong p-4">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-foreground flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-background" aria-hidden="true" strokeWidth={2} />
              </div>
              <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                {language === "de" ? "Nearshore-Einblick" : "Nearshore Insight"}
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 rounded-full"
              onClick={dismiss}
              aria-label={language === "de" ? "Einblick schließen" : "Dismiss insight"}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>

          {isLoadingInsight || isSearching ? (
            <div className="space-y-2">
              <div className="h-2.5 bg-muted animate-pulse rounded-full w-full" />
              <div className="h-2.5 bg-muted animate-pulse rounded-full w-4/5" />
              <div className="h-2.5 bg-muted animate-pulse rounded-full w-3/5" />
            </div>
          ) : insight ? (
            <>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {insight}
              </p>
              <Button variant="outline" size="sm" className="w-full text-xs" asChild>
                <a
                  href="https://www.evolvice.de/en"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={
                    language === "de"
                      ? "Gespräch mit Evolvice GmbH starten (öffnet neues Fenster)"
                      : "Start a conversation with Evolvice GmbH (opens new tab)"
                  }
                >
                  {language === "de" ? "Gespräch starten" : "Start a conversation"}
                  <ChevronRight className="ml-1 h-3 w-3" aria-hidden="true" />
                </a>
              </Button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {language === "de" ? "Kein Einblick verfügbar." : "No insight available."}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default ContextualDiscovery;
